import { ServicesUpdateDto } from 'src/dtos/default-service-update.dto';
import { UpdatedCountryDto } from 'src/dtos/update-country.dto';
import {
    FwVersion,
    FwVersionContentAndMetadata,
    FwVersionInfo,
    PolicySummaryForDashboardDto
} from 'src/dtos/version.dto';
import axiosService from './axios-service';
import { BulkRecommendationDto, PolicyBulkUpdateDto } from '../dtos/policy-bulk-update.dto';
import { PolicySummaryDto } from '../dtos/policy-summary.dto';

class FwVersionService {

    public getVersionsList = async (customerId: number, sid: number): Promise<{ [key: string]: FwVersionInfo[] }> => {
        const result = await axiosService.get(`/api/firewall/customer/${customerId}/service/${sid}/all-versions`);
        return result?.data;
    };

    public getVersionData = async (customerId: number, sid: number, versionId: string): Promise<FwVersion> => {
        const result = await axiosService.get(`/api/firewall/customer/${customerId}/service/${sid}/version/${versionId}`,false);
        return result?.data;
    };

    public deleteVersion = async (customerId: number, sid: number, versionId: string): Promise<FwVersion> => {
        const result = await axiosService.delete(`/api/firewall/customer/${customerId}/service/${sid}/version/${versionId}`);
        return result?.data;
    };

    public updateVersionComment = async (customerId: number, sid: number, externalId: string, policyComments: string): Promise<void> => {
        await axiosService.put(`/api/firewall/customer/${customerId}/service/${sid}/version/comments`, {
            externalId,
            comments: policyComments || null
        });
    };

    public updateVersionInfo = async (customerId: number, sid: number, externalId: string, policyComments: string, versionName: string): Promise<void> => {
        await axiosService.put(`/api/firewall/customer/${customerId}/service/${sid}/version/update-info`, {
            externalId,
            comments: policyComments || null,
            versionName
        });
    };

    public updateBlockCountry = async (customerId: number, sid: number, externalId: string, updatedCountryDto: UpdatedCountryDto[]): Promise<FwVersionContentAndMetadata> => {
        const result = await axiosService.put(`/api/firewall/customer/${customerId}/service/${sid}/country-filtering`, {
            externalId,
            updatedCountryDtoList: updatedCountryDto
        });
        return result?.data;
    };

    public updateBlockCategory = async (customerId: number, sid: number, externalId: string,
        profileName: string, blockCategoryList: string[]): Promise<FwVersionContentAndMetadata> => {
        const result = await axiosService.put(`/api/firewall/customer/${customerId}/service/${sid}/categoriesProfileUpdate`, {
            externalId,
            profileName,
            blockedCategoryNameList: blockCategoryList
        });
        return result?.data;
    };

    public updateDefaultServiceAndApp = async (customerId: number, sid: number, externalId: string, serviceUpdateDto: ServicesUpdateDto): Promise<FwVersionContentAndMetadata> => {
        const result = await axiosService.put(`/api/firewall/customer/${customerId}/service/${sid}/default-service-update`, {
            externalId,
            serviceUpdateDto
        });
        return result?.data;
    };

    public commitAndPushVersion = async (customerId: number, sid: number, externalId: string, versionName?: string, policyComments?: string):
        Promise<FwVersion> => {
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/version-action`, {
            externalId,
            comments: policyComments || null,
            versionName: versionName || null
        });
        return result?.data;
    };
    public createNewDraftVersion = async (customerId: number, externalId: string, sid: number, isRestore: boolean): Promise<FwVersion> => {
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/firewall-version`, {
            comments: '',
            baseVersionExternalId: externalId,
            restore: isRestore
        });
        return result?.data;
    };

    public async restorePolicyAndApply(customerId: number, serviceId: number, baseVersionExternalId: string, versionName?: string, comments?: string): Promise<FwVersion> {
        const response = await axiosService.post(`/api/firewall/customer/${customerId}/service/${serviceId}/restore-policy-and-apply`, {
            baseVersionExternalId,
            versionName,
            comments
        });
        return response.data;
    }

    public async pullPolicyAndApply(customerId: number,
                                    serviceId: number,
                                    sourceServiceId: number,
                                    sourceServiceName: string,
                                    versionName?: string,
                                    comments?: string): Promise<FwVersion> {
        const response = await axiosService.post(`/api/firewall/customer/${customerId}/service/${serviceId}/pull-policy-and-apply`, {
            sourceServiceId,
            sourceServiceName,
            policyData: {
                versionName,
                comments
            }
        });
        return response.data;
    }

    public bulkUpdatePolicyBasedOnRecommendation = async (
        customerId: number, serviceId: number, policyBulkRecommendation: BulkRecommendationDto): Promise<FwVersionContentAndMetadata> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/service/${serviceId}/bulk-recommendation-update`,
            policyBulkRecommendation
        );
        return res?.data;
    };

    public bulkUpdatePolicyBasedOnSelection = async (customerId: number, serviceId: number, policyBulkUpdate: PolicyBulkUpdateDto): Promise<FwVersionContentAndMetadata> => {
        const res = await axiosService.post(`/api/firewall/customer/${customerId}/service/${serviceId}/bulk-update`,
            policyBulkUpdate
        );
        return res?.data;
    };

    public getPolicySummaryForDashboard = async (customerId: number, serviceId: number): Promise<PolicySummaryForDashboardDto> => {
        const result = await axiosService.get(`/api/firewall/customer/${customerId}/service/${serviceId}/policy-summary`);
        return result?.data;
    };

    public getPolicySummary = async (customerId: number, serviceId: number, externalId: string): Promise<PolicySummaryDto> => {
        const result = await axiosService.get(`/api/firewall/customer/${customerId}/service/${serviceId}/policy-summary/${externalId}`, false);
        return result?.data;
    };

    public pullVersion = async (customerId: number, sid: number, sourceServiceId: number, sourceCircuitId: string): Promise<FwVersion> => {
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/pull-policy`,
            {
                sourceServiceId: sourceServiceId,
                sourceServiceName: sourceCircuitId
            });
        return result?.data;
    };

    public getAvailableConnectionsForPushPolicy = async (customerId: number, serviceId: number, compareActiveVersion: boolean, isScheduled: boolean) => {
        const result = await axiosService.get(`/api/firewall/customer/${customerId}/service/${serviceId}/distribute-policy-state` +
            `?compareActiveVersion=${compareActiveVersion}&isSchedule=${isScheduled}`, false);
        return result?.data;
    };

    async pushPolicy(customerId: number, sid: number, selectedConnections: number[], toApply: boolean) {
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/push-policy`,
            {
                destinationServiceIdList: selectedConnections,
                toApply
            });
        return result?.data;
    }

    async applyPolicyWithOtherConnections(customerId: number, sid: number, externalId: string, selectedConnections: number[],
                                          versionName?: string, policyComments?: string): Promise<FwVersion> {
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/apply-with-others`,
            {
                destinationServiceIdList: selectedConnections,
                applyPolicyInfo : {
                    externalId,
                    versionName,
                    comments : policyComments
                }
            });
        return result?.data;
    }

    public activateVersion = async (customerId: number, sid: number, isDeactivate: boolean): Promise<FwVersionContentAndMetadata> => {
        const deactivatePath = isDeactivate ? 'deactivate' : 'reactivate';
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/${deactivatePath}`);
        return result?.data;
    };

    public scheduleSinglePolicy = async (customerId: number,
                                         sid: number,
                                         externalId: string,
                                         scheduleDate: string,
                                         versionName?: string,
                                         comments?: string): Promise<FwVersion> => {
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/schedule-single-policy`, {
            externalId,
            comments,
            versionName,
            scheduledApplyPolicyDto: {
                scheduleDate
            }
        });
        return result?.data;
    };

    public discardSchedulePolicy = async (customerId: number,
                                                                  sid: number) => {
        await axiosService.delete(`/api/firewall/customer/${customerId}/service/${sid}/discard-schedule-policy`);
    };

    public scheduleMultipleConnections = async (customerId: number,
                                                sid: number,
                                                externalId: string,
                                                selectedConnections: number[],
                                                scheduleDate: string,
                                                versionName?: string,
                                                comments?: string): Promise<FwVersion> => {
        const result = await axiosService.post(`/api/firewall/customer/${customerId}/service/${sid}/schedule-multiple-policies`, {
            destinationServiceIdList: selectedConnections,
            applyPolicyInfo: {
                externalId,
                comments,
                versionName
            },
            scheduledApplyPolicyDto: {
                scheduleDate
            }
        });
        return result?.data;
    };
}

const fwVersionService = new FwVersionService();
export default fwVersionService;

